/**
 * ListingLoadingLabel — spinner + honest text for an in-flight object listing.
 *
 * Soporte C3 (`SoporteIssues2.md` point 3): the Data/Schema Compare setup
 * dialogs showed a bare "Loading…" in their Database and Schema dropdowns.
 * On a cold remote MSSQL connection that spinner could sit there for minutes
 * while the driver connected, with no way to tell "still reaching the server"
 * from "connected, reading the list" — the user's report was a dead-end
 * dialog they had to Cancel.
 *
 * `getDatabases` reports a {@link DatabasesListingPhase} (`'connecting'` while
 * the MSSQL driver pool warms up, `'listing'` once the query is running), so
 * the database spinner says which one it is. `getTables` has no separable
 * connect step, so table/schema spinners use a single static label rather than
 * inventing a phase they can't observe.
 */

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { DatabasesListingPhase } from '@/features/database-objects/services/databasesService';

type ListingLoadingLabelProps = {
  /** What is being listed once the connect step (if any) is done. */
  target: 'databases' | 'tables' | 'schemas';
  /**
   * Phase reported by the listing service. Only database listings report one;
   * leave it undefined for table/schema listings.
   */
  phase?: DatabasesListingPhase;
  /** Spinner size in px — matches the surrounding control's density. */
  size?: number;
};

const TARGET_LABEL: Record<ListingLoadingLabelProps['target'], string> = {
  databases: 'Loading databases…',
  tables: 'Loading tables…',
  schemas: 'Loading schemas…',
};

export function ListingLoadingLabel({ target, phase, size = 12 }: ListingLoadingLabelProps) {
  return (
    <span className="flex items-center gap-2 text-muted-foreground">
      <RandomDotmSpinner size={size} />
      {phase === 'connecting' ? 'Connecting to server…' : TARGET_LABEL[target]}
    </span>
  );
}
